import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { Menu, X } from "lucide-react";
import { SITE } from "@/content/copy";
import { trackEvent } from "@/lib/analytics";

const NAV = [
  { label: "Services", href: "/#services" },
  { label: "How it works", href: "/#how-it-works" },
  { label: "About", href: "/#about" },
  { label: "FAQ", href: "/#faq" },
];

export default function Header() {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 8);
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <header
      data-testid="site-header"
      className={`sticky top-0 z-50 bg-background/85 backdrop-blur-md transition-shadow duration-300 ${
        scrolled ? "shadow-sm border-b border-border" : ""
      }`}
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-16">
          <Link to="/" data-testid="header-logo" className="flex items-center gap-2">
            <span className="w-8 h-8 rounded-lg bg-navy flex items-center justify-center" aria-hidden="true">
              <svg viewBox="0 0 64 64" className="w-5 h-5">
                <path d="M40 22a12 12 0 1 0 0 20" stroke="#D97757" strokeWidth="7" fill="none" strokeLinecap="round" />
              </svg>
            </span>
            <span className="font-extrabold text-navy text-lg tracking-tight">Clarus Partners</span>
          </Link>

          <nav className="hidden md:flex items-center gap-8" aria-label="Main">
            {NAV.map((item) => (
              <a
                key={item.href}
                href={item.href}
                data-testid={`nav-${item.label.toLowerCase().replace(/\s+/g, "-")}`}
                className="text-sm font-semibold text-navy/70 hover:text-navy transition-colors duration-200"
              >
                {item.label}
              </a>
            ))}
          </nav>

          <div className="flex items-center gap-3">
            <a
              href={SITE.calendlyUrl}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="header-book-call-button"
              onClick={() => trackEvent("book_consultation_click", { location: "header" })}
              className="inline-flex items-center rounded-full bg-terracotta px-4 py-2 sm:px-5 sm:py-2.5 text-xs sm:text-sm font-bold text-white transition-colors duration-200 hover:bg-terracotta-hover focus-visible:ring-2 focus-visible:ring-terracotta focus-visible:ring-offset-2 focus-visible:outline-none"
            >
              Book a call
            </a>
            <button
              type="button"
              data-testid="mobile-menu-toggle"
              aria-label={open ? "Close menu" : "Open menu"}
              onClick={() => setOpen(!open)}
              className="md:hidden p-2 text-navy"
            >
              {open ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
            </button>
          </div>
        </div>
      </div>

      {open && (
        <nav className="md:hidden border-t border-border bg-background" aria-label="Mobile">
          <div className="px-4 py-4 flex flex-col gap-4">
            {NAV.map((item) => (
              <a
                key={item.href}
                href={item.href}
                data-testid={`mobile-nav-${item.label.toLowerCase().replace(/\s+/g, "-")}`}
                onClick={() => setOpen(false)}
                className="text-base font-semibold text-navy/80"
              >
                {item.label}
              </a>
            ))}
            <a
              href={SITE.calendlyUrl}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="mobile-book-call-button"
              onClick={() => trackEvent("book_consultation_click", { location: "mobile_menu" })}
              className="inline-flex justify-center rounded-full bg-terracotta px-5 py-3 text-sm font-bold text-white"
            >
              {SITE.consultLabel}
            </a>
          </div>
        </nav>
      )}
    </header>
  );
}
